'use client';
import React, { useCallback, useContext, useMemo, useState, JSX} from 'react';
import { ThemeProvider } from 'styled-components';
import { getTheme } from '../styles/theme';
import { getColors, schemes, ColorProps, Scheme } from '@osmosis/shared/styles';

type SchemeContextProps = {
  colors: ColorProps;
  scheme: Scheme;
  setScheme: (newScheme: Scheme) => void;
};

const SchemeContext = React.createContext<SchemeContextProps>({
  colors: schemes.light,
  scheme: 'light',
  setScheme: () => {
    /* no-op */
  },
} as SchemeContextProps);

export function SchemeProvider({
  children,
  scheme: schemeProps = 'light',
}: {
  children: React.ReactNode;
  scheme?: Scheme;
}): JSX.Element {
  const [scheme, setScheme] = useState(schemeProps);
  const colors = getColors(scheme);

  const handleSetScheme = useCallback((newScheme: Scheme) => {
    if (newScheme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }

    setScheme(newScheme);
  }, []);

  const value = useMemo(
    () => ({ scheme, setScheme: handleSetScheme, colors }),
    [scheme, handleSetScheme, colors],
  );

  return (
    <SchemeContext.Provider value={value}>
      <ThemeProvider theme={getTheme({ scheme })}>{children}</ThemeProvider>
    </SchemeContext.Provider>
  );
}

export const useSchemeContext = (): SchemeContextProps =>
  useContext(SchemeContext);
